import { Skeleton, Box, Divider, Text } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';

import { useGetUserWithEmailSubscriptionsQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { EmailSubscriptionsViewContainer } from './EmailSubscriptionsViewContainer';
import { SlackInstallationsView } from './SlackInstallationsView';
import { Typography } from 'packages/common-frontend/components/Typography';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { PermissionGuard, PermissionsFn } from '~/core/PermissionGuard';
import { PageHeader } from '~/packages/common-frontend/components/PageContainer';
import { useGetCreditFacilitiesLazyQueryGQLType } from '~/packages/credit-facility/requests/requests.gql.generated';

// Fragments
const RouteBase = () => {
  const userQuery = useGetUserWithEmailSubscriptionsQuery();

  const [
    getCreditFacilities,
    creditFacilitiesQuery,
  ] = useGetCreditFacilitiesLazyQueryGQLType();

  useEffect(() => {
    if (userQuery.data?.user?.organizationId) {
      getCreditFacilities({
        variables: { organizationId: userQuery.data.user.organizationId },
      });
    }
  }, [userQuery.data?.user?.organizationId, getCreditFacilities]);

  if (
    userQuery.loading ||
    !userQuery.data?.user ||
    creditFacilitiesQuery.loading ||
    !creditFacilitiesQuery.data
  ) {
    return <Skeleton height="600px" data-testid="settings-route-loading" />;
  }

  const queryError = userQuery.error || creditFacilitiesQuery.error;
  if (queryError) throw queryError;

  return (
    <Box>
      <Typography variant="page-title">Notifications</Typography>
      <PageHeader headerLabel="Manage Notifications" />
      <Text width="100%" mt="10px" fontSize="sm" align="left">
        Manage all slack and email notification settings across all Finley
        Dashboard features
      </Text>
      <Box>
        <Divider m="25px 0" orientation="horizontal" />
        <EmailSubscriptionsViewContainer
          creditFacilitySlugs={creditFacilitiesQuery.data.creditFacilities.map(
            (creditFacility) => creditFacility.slug,
          )}
          emailSubscriptions={userQuery.data.user.emailSubscriptions}
        />
        <Divider m="25px 0" orientation="horizontal" />
        <SlackInstallationsView />
      </Box>
    </Box>
  );
};

// Exports
export const permissions: PermissionsFn = (context) => {
  if (context.user.userType !== 'BORROWER') return 'deny';
  if (context.flags.isBorrowerLimitedOrganization) return 'upsell';
  return 'allow';
};
export const Route = () => (
  <PermissionGuard
    permissions={permissions}
    fallback={<Navigate to="/settings" replace />}
  >
    <RouteErrorBoundary
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <Navigate to="/settings" />;
      }}
    >
      <RouteBase />
    </RouteErrorBoundary>
  </PermissionGuard>
);
